/**
 * Boot log analysis.
 *
 * A flash can succeed byte-for-byte and still leave a board that will not run,
 * and the ROM's explanation of why is a line like "invalid header: 0xffffffff"
 * that means nothing to most people. These matchers watch the serial monitor
 * for the signatures that follow a bad flash and translate them.
 *
 * Signatures come from the ESP-IDF first/second stage bootloaders
 * (bootloader_start.c, esp_image_format.c) and the panic handler.
 */

export interface BootFinding {
  id: string;
  severity: "error" | "warning";
  title: string;
  explanation: string;
  fixes: string[];
}

interface BootMatcher {
  id: string;
  pattern: RegExp;
  severity: "error" | "warning";
  title: string;
  explanation: string;
  fixes: string[];
}

const MATCHERS: BootMatcher[] = [
  {
    id: "invalid-header",
    pattern: /invalid header:?\s*0x[0-9a-f]+/i,
    severity: "error",
    title: "The bootloader found no valid application",
    explanation:
      "The second-stage bootloader jumped to where the application should be and found something that is not a " +
      "valid image. The bytes there are either erased flash (0xffffffff) or the wrong data entirely.",
    fixes: [
      "The application is almost certainly at the wrong offset. On ESP32 it belongs at 0x10000 — check the offset shown next to your sketch .bin.",
      "If you erased the whole flash, the partition table went with it. Re-flash the bootloader, partition table, and application together.",
      "Confirm you flashed the sketch .bin and not the .bootloader.bin or .partitions.bin by mistake.",
    ],
  },
  {
    id: "flash-read-err",
    pattern: /flash read err|invalid magic byte|checksum err/i,
    severity: "error",
    title: "The bootloader could not read the flash",
    explanation: "The bootloader failed to read back the region it needs to boot from.",
    fixes: [
      "Re-flash with the flash mode set to `dio` rather than `qio`. Some modules cannot run in quad mode.",
      'Enable "Erase entire flash" and write the bootloader, partition table, and application together.',
      "If it persists, the flash chip may be failing.",
    ],
  },
  {
    id: "brownout",
    pattern: /brownout detector was triggered/i,
    severity: "error",
    title: "The board browned out",
    explanation:
      "Supply voltage dropped below the safe threshold and the chip reset itself. This usually happens the moment " +
      "Wi-Fi or Bluetooth switches on, because the radio draws a large current spike.",
    fixes: [
      "Plug into a port that can supply more current, or use a powered USB hub. Many laptop ports and hubs cannot.",
      "Replace the USB cable — a thin or long cable drops enough voltage to cause this on its own.",
      "If the board is powered from a regulator or battery, check it can deliver at least 500 mA in bursts.",
    ],
  },
  {
    id: "guru-meditation",
    pattern: /guru meditation error/i,
    severity: "error",
    title: "The firmware crashed",
    explanation:
      "The application started but hit a fatal exception. The flash itself is fine — this is a bug in the sketch, " +
      "not a flashing problem.",
    fixes: [
      "Read the register dump and backtrace below; the exception name (LoadProhibited, StoreProhibited, IllegalInstruction) tells you the class of bug.",
      "LoadProhibited or StoreProhibited almost always means a null or dangling pointer.",
      "Decode the backtrace addresses to line numbers with `xtensa-esp32-elf-addr2line -pfiaC -e your_sketch.ino.elf <addresses>`.",
    ],
  },
  {
    id: "watchdog",
    pattern: /task_wdt|watchdog got triggered|rtcwdt_rtc_reset/i,
    severity: "error",
    title: "A watchdog timer reset the board",
    explanation:
      "Code blocked for too long without yielding, so the watchdog assumed it had hung and reset the chip.",
    fixes: [
      "Look for a long-running loop that never calls delay() or yield().",
      "Blocking network calls without a timeout are the usual cause.",
    ],
  },
  {
    id: "boot-loop",
    pattern: /(rst:0x[0-9a-f]+[\s\S]{0,400}){4,}/i,
    severity: "warning",
    title: "The board looks like it is resetting repeatedly",
    explanation:
      "Several reset banners appeared in quick succession, which means the board is not getting far enough to run " +
      "your code before something restarts it.",
    fixes: [
      "Look at the lines just above each reset — the reason is usually printed there.",
      "A brownout, a crash in setup(), or a missing partition table all produce this pattern.",
    ],
  },
  {
    id: "no-partition-table",
    pattern: /partition table.*(invalid|md5|error)|no partition table/i,
    severity: "error",
    title: "The partition table is missing or corrupt",
    explanation: "The bootloader could not read a valid partition table at 0x8000.",
    fixes: [
      "Flash the partitions .bin from your Arduino export at offset 0x8000.",
      "If you only wrote the application, add the bootloader and partition table and flash all three together.",
    ],
  },
];

/**
 * Scan monitor output for known boot failures.
 * Only the tail is examined so a long session stays cheap to re-scan.
 */
export function analyzeBootLog(text: string): BootFinding[] {
  if (text.length === 0) return [];

  const tail = text.length > 8000 ? text.slice(-8000) : text;

  return MATCHERS.filter((matcher) => matcher.pattern.test(tail)).map(
    ({ pattern: _pattern, ...finding }) => finding,
  );
}
